import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { ASSETS } from "@/lib/site";

const NAV = [
  { to: "/tedaviler", label: { tr: "Tedaviler", en: "Treatments" } },
  { to: "/yolculuk", label: { tr: "Yolculuk", en: "Your Journey" } },
  { to: "/klinik", label: { tr: "Klinik", en: "Clinic" } },
  { to: "/hekimler", label: { tr: "Hekimler", en: "Doctors" } },
  { to: "/sonuclar", label: { tr: "Sonuçlar", en: "Results" } },
  { to: "/sss", label: { tr: "SSS", en: "FAQ" } },
  { to: "/iletisim", label: { tr: "İletişim", en: "Contact" } },
];

export function SiteHeader() {
  const { t, lang, setLang, guide, setGateOpen } = useApp();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${
        scrolled || open ? "bg-[#05070A]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center" data-testid="header-logo">
          <span className="h-10 w-[130px] overflow-hidden flex items-center justify-center">
            <img src={ASSETS.logoWhite} alt="Zeta Clinic" className="w-[150px] max-w-none" />
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Ana menü">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[var(--accent-2)]" : "text-[#94A3AE] hover:text-[#F2F5F7]"}`
              }
            >
              {t(n.label)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full border border-white/15 overflow-hidden text-xs font-semibold" data-testid="lang-switcher">
            {["tr", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 uppercase transition-colors ${lang === l ? "bg-[var(--accent)] text-white" : "text-[#94A3AE] hover:text-[#F2F5F7]"}`}
              >
                {l}
              </button>
            ))}
          </div>

          <button
            onClick={() => setGateOpen(true)}
            data-testid="guide-avatar-btn"
            aria-label={t({ tr: "Rehber seç", en: "Choose guide" })}
            className="hidden sm:flex items-center gap-2 rounded-full border border-[var(--accent)]/40 bg-[var(--accent-soft)] px-3 py-1.5 text-xs font-semibold text-[var(--accent-2)] hover:border-[var(--accent-2)] transition-colors"
          >
            <span className="w-2 h-2 rounded-full bg-[var(--accent-2)]" />
            {guide ? guide.toUpperCase() : t({ tr: "Rehber", en: "Guide" })}
          </button>

          <Link to="/mirror" data-testid="header-mirror-cta" className="hidden md:inline-flex cta-primary !px-4 !py-2">
            <Sparkles size={15} /> ZETA Mirror
          </Link>

          <button className="lg:hidden p-2 text-[#F2F5F7]" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menü">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden bg-[#05070A]/95 backdrop-blur-xl border-t border-white/10 px-6 py-4 flex flex-col gap-3" data-testid="mobile-menu">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-sm font-medium text-[#94A3AE] py-1.5 hover:text-[#F2F5F7]">
              {t(n.label)}
            </NavLink>
          ))}
          <Link to="/mirror" onClick={() => setOpen(false)} className="mt-2 cta-primary justify-center">
            ZETA Mirror
          </Link>
        </nav>
      )}
    </header>
  );
}
